import React, { useEffect, useState } from "react";
import { GripVertical, ImageIcon, Layers, LayoutGrid, Plus } from "lucide-react";
import { cn } from "@/modern/utils";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Sortable, SortableContent, SortableItem, SortableItemHandle } from "@/modern/components/ui/sortable";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

interface WorkFileCardListProps {
  cards: MarketingWorkFileCard[];
  selectedCardId: number | null;
  onSelectCard: (id: number | null) => void;
  onAddCard?: () => void;
  isAddingCard?: boolean;
  onReorderCards?: (reorderedCards: MarketingWorkFileCard[]) => void;
}

export const WorkFileCardList = ({
  cards,
  selectedCardId,
  onSelectCard,
  onAddCard,
  isAddingCard: _isAddingCard = false,
  onReorderCards,
}: WorkFileCardListProps) => {
  const [items, setItems] = useState<MarketingWorkFileCard[]>(cards);

  useEffect(() => {
    setItems(cards);
  }, [cards]);

  return (
    <aside className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-background">
      {/* Sticky sub-header */}
      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-border bg-card px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          Sections
          <span className="text-muted-foreground">· {cards.length}</span>
        </span>
        <button
          type="button"
          aria-label="Add card"
          onClick={onAddCard}
          className="inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-3 w-3" />
          New
        </button>
      </div>

      {/* Scrollable list */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-dashed border-border bg-muted/40 text-muted-foreground">
              <LayoutGrid className="h-6 w-6" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground">No cards yet</p>
              <p className="max-w-[200px] text-xs text-muted-foreground">
                Add your first card to start building this work file.
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs"
              onClick={onAddCard}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Card
            </Button>
          </div>
        ) : (
          <Sortable
            value={items}
            onValueChange={(reordered) => {
              setItems(reordered);
              onReorderCards?.(reordered);
            }}
            getItemValue={(item) => item.id}
          >
            <SortableContent className="space-y-2.5 p-3">
              {items.map((card) => (
                <SortableItem key={card.id} value={card.id}>
                  <WorkFileCardListItem
                    card={card}
                    selected={card.id === selectedCardId}
                    onSelect={() => onSelectCard(card.id)}
                  />
                </SortableItem>
              ))}
            </SortableContent>
          </Sortable>
        )}
      </div>
    </aside>
  );
};

interface WorkFileCardListItemProps {
  card: MarketingWorkFileCard;
  selected: boolean;
  onSelect: () => void;
}

const WorkFileCardListItem = ({ card, selected, onSelect }: WorkFileCardListItemProps) => {
  const { project } = useSwivuBryntumContext();

  const firstThumbSrc = (() => {
    const assetStore = project?.getCrudStore(STORE_KEYS.marketingAssetStore);
    if (!assetStore || !card.assets?.length) return null;
    const asset = assetStore.getById(card.assets[0]) as unknown as MarketingAsset | undefined;
    const boardFile = asset?.fileRecord as unknown as BoardFile | undefined;
    return (boardFile as any)?.url ?? (boardFile as any)?.thumbnailUrl ?? null;
  })();

  const assetCount = card.assets?.length ?? 0;
  const assetLabel = `${assetCount} asset${assetCount === 1 ? "" : "s"}`;

  return (
    <li
      className={cn(
        "group/row flex items-stretch overflow-hidden rounded-lg border-y border-r transition-all duration-150",
        "border-l-[3px]",
        selected
          ? "border-l-primary border-y-primary/20 border-r-primary/20 bg-primary/10"
          : "border-l-border/70 border-y-border/70 border-r-border/70 bg-card hover:border-l-primary/40 hover:bg-primary/5",
      )}
    >
      {/* Drag handle — inside the card on the left */}
      <SortableItemHandle value={card.id}>
        <button
          type="button"
          aria-label="Drag to reorder"
          className="flex w-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground/0 transition-colors group-hover/row:text-muted-foreground/40 hover:!text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      </SortableItemHandle>

      {/* Clickable card content */}
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex min-w-0 flex-1 items-start gap-3 py-3 pr-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {firstThumbSrc ? (
          <img
            src={firstThumbSrc}
            alt=""
            className="h-10 w-10 shrink-0 rounded-md border border-border object-cover shadow-sm"
          />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-dashed border-border bg-muted/40 text-muted-foreground/50">
            <ImageIcon className="h-4 w-4" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "line-clamp-2 text-sm font-medium leading-snug",
              card.heading?.trim() ? "text-foreground" : "text-muted-foreground/40",
            )}
          >
            {card.heading?.trim() || "Heading"}
          </div>
          {card.heading?.trim() ? (
            card.subHeading?.trim() && (
              <p className="mt-0.5 truncate text-xs leading-snug text-muted-foreground">
                {card.subHeading}
              </p>
            )
          ) : (
            <p className="mt-0.5 truncate text-xs leading-snug text-muted-foreground/30">
              Sub Heading
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
            <ImageIcon className="h-3 w-3" />
            <span>{assetLabel}</span>
          </div>
        </div>
      </button>
    </li>
  );
};

export default WorkFileCardList;
